import { IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import Link from '@/elements/Link';
import { WorkerDetail } from '@/services/workersV2API';
import { IS_PROD } from '@/constants/env';
import Config from '@/config';
import { EXTERNAL_URL } from '@/util/constants';
import {
  getAccountStatusBadgeStatus,
  getAccountStatusLabel,
  getSuspensionStatusBadgeStatus,
  getSuspensionStatusLabel,
} from '@/util/workerStatus';

type Props = {
  worker: WorkerDetail;
  checkrCandidateId?: string | null;
  zealEmployeeId?: string | null;
  checkEmployeeId?: string | null;
  onEditAddress?: () => void;
};

const CHECKR_BASE_URL = IS_PROD
  ? 'https://dashboard.checkr.com'
  : 'https://dashboard.checkrhq-staging.net';

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateOnly = (dateString: string | null | undefined) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const formatStatus = (status: string | null | undefined) => {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Stack
    style={{
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #F3F4F6',
    }}
  >
    <Small color="lighter">{label}</Small>
    <Body>{value}</Body>
  </Stack>
);

const WorkerDetailsCard = ({
  worker,
  checkrCandidateId,
  zealEmployeeId,
  checkEmployeeId,
  onEditAddress,
}: Props) => {
  const address = worker.address;
  const fullAddress = address
    ? [
        address.address_line1,
        address.address_line2,
        address.city,
        address.state,
        address.zip,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const stats = worker.stats;
  const ratings = worker.ratings;
  const statuses = worker.statuses;
  const dates = worker.dates;
  const user = worker.user;
  const checkHQUrl = checkEmployeeId
    ? (worker.tax_type === 'w2' ? EXTERNAL_URL.w2 : EXTERNAL_URL[1099])
        .replace('{0}', Config.CHECKHQ_COMPANY)
        .replace('{1}', checkEmployeeId)
    : null;

  return (
    <Card title="Worker Details">
      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Links</Body>
          <Stack vertical gap={4}>
            <Stack
              style={{
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <Small color="lighter">V1 Page</Small>
              <Link to={`/workers/${worker.id}`}>workers/{worker.id}</Link>
            </Stack>
            <Stack
              style={{
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <Small color="lighter">Checkr</Small>
              {checkrCandidateId ? (
                <Link
                  to={`${CHECKR_BASE_URL}/candidates/${checkrCandidateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View in Checkr
                </Link>
              ) : (
                <Small color="lighter">Pending</Small>
              )}
            </Stack>
            <Stack
              style={{
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <Small color="lighter">Zeal</Small>
              {zealEmployeeId ? (
                <Link
                  to={`https://app.zeal.com/employees/${zealEmployeeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View in Zeal
                </Link>
              ) : (
                <Small color="lighter">Pending</Small>
              )}
            </Stack>
            <Stack
              style={{
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <Small color="lighter">CheckHQ</Small>
              {checkHQUrl ? (
                <Link to={checkHQUrl} target="_blank" rel="noopener noreferrer">
                  View in CheckHQ
                </Link>
              ) : (
                <Small color="lighter">Pending</Small>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={4}>
          <DetailRow label="Worker ID" value={worker.id} />
          <DetailRow label="Email" value={user?.email || 'N/A'} />
          <DetailRow label="Phone" value={user?.phone_number || 'N/A'} />
          <DetailRow
            label="Date of Birth"
            value={formatDateOnly(user?.date_of_birth)}
          />
          <DetailRow
            label="Language"
            value={
              user?.language
                ? user.secondary_language
                  ? `${user.language.toUpperCase()} / ${user.secondary_language.toUpperCase()}`
                  : user.language.toUpperCase()
                : 'N/A'
            }
          />
          <DetailRow
            label="Address"
            value={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                }}
              >
                <span>{fullAddress || 'N/A'}</span>
                {onEditAddress && (
                  <IconButton
                    onClick={onEditAddress}
                    size="small"
                    title="Edit Address"
                    style={{ padding: 2 }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </div>
            }
          />
          <DetailRow
            label="Tax Type"
            value={
              <Badge
                label={worker.tax_type?.toUpperCase() || 'N/A'}
                status={worker.tax_type === 'w2' ? 'theme' : 'neutral'}
              />
            }
          />
          <DetailRow
            label="Account Status"
            value={
              <Badge
                label={getAccountStatusLabel(worker)}
                status={getAccountStatusBadgeStatus(worker)}
              />
            }
          />
          <DetailRow
            label="Suspension Status"
            value={
              <Badge
                label={getSuspensionStatusLabel(worker)}
                status={getSuspensionStatusBadgeStatus(worker)}
              />
            }
          />
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Statuses</Body>
          <Stack vertical gap={4}>
            <DetailRow
              label="Onboarding"
              value={
                <Badge
                  label={formatStatus(statuses?.onboarding_status)}
                  status={
                    statuses?.onboarding_status === 'onboarding_completed'
                      ? 'theme'
                      : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="I-9"
              value={
                <Badge
                  label={formatStatus(statuses?.i9_status)}
                  status={
                    statuses?.i9_status === 'approved' ? 'theme' : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="Background"
              value={
                <Badge
                  label={formatStatus(statuses?.background_status)}
                  status={
                    statuses?.background_status === 'approved'
                      ? 'theme'
                      : 'warning'
                  }
                />
              }
            />
            <DetailRow
              label="Ready to Work"
              value={
                <Badge
                  label={statuses?.active_and_ready_to_work ? 'Yes' : 'No'}
                  status={
                    statuses?.active_and_ready_to_work ? 'theme' : 'neutral'
                  }
                />
              }
            />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Stats</Body>
          <Stack vertical gap={4}>
            <DetailRow
              label="Shifts Worked"
              value={stats?.jobs_worked ?? 'N/A'}
            />
            <DetailRow
              label="Shifts Dropped"
              value={stats?.jobs_dropped ?? 'N/A'}
            />
            <DetailRow label="No Shows" value={stats?.no_show ?? 'N/A'} />
            <DetailRow
              label="Average Rating"
              value={
                ratings?.average_rating
                  ? `${ratings.average_rating.toFixed(1)} (${ratings.jobs_rating_count ?? 0} reviews)`
                  : 'N/A'
              }
            />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Dates</Body>
          <Stack vertical gap={4}>
            <DetailRow label="Created" value={formatDate(worker.created_at)} />
            <DetailRow
              label="Onboarding Completed"
              value={formatDate(dates?.onboarding_completed_at)}
            />
            <DetailRow
              label="Approved"
              value={formatDate(dates?.approved_at)}
            />
          </Stack>
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default WorkerDetailsCard;
